/**
 * Cognito id_token claim checks (iss, aud, exp, nonce, token_use).
 *
 * The signature is deliberately NOT verified here. The id_token is obtained over TLS
 * straight from the Cognito token endpoint via the PKCE code exchange, so OIDC Core
 * 3.1.3.7 lets the client skip signature validation. Verifying it in the browser would
 * also require a cross-site call to the Cognito jwks_uri, which corporate egress blocks
 * with 403. The authoritative signature check runs server-side in src/server/cid.
 */

import { getCognitoConfig } from './config';

const base64UrlToString = (segment) => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(pad);
  return decodeURIComponent(
    atob(padded)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
};

const decodeSegment = (segment) => JSON.parse(base64UrlToString(segment));

/** Validate a Cognito id_token: iss, aud, exp, nonce, and token_use. */
export const validateIdToken = async (idToken, { nonce, clientId } = {}) => {
  if (!idToken) {
    throw new Error('Missing id_token');
  }
  const parts = idToken.split('.');
  if (parts.length !== 3) {
    throw new Error('Malformed id_token');
  }

  const header = decodeSegment(parts[0]);
  if (header.alg !== 'RS256') {
    throw new Error(`Unsupported id_token alg: ${header.alg}`);
  }

  const claims = decodeSegment(parts[1]);
  const { issuer } = getCognitoConfig();
  const expectedClientId = clientId || getCognitoConfig().clientId;

  if (issuer && claims.iss !== issuer) {
    throw new Error('id_token iss mismatch');
  }
  const aud = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
  if (!aud.includes(expectedClientId)) {
    throw new Error('id_token aud mismatch');
  }
  const now = Math.floor(Date.now() / 1000);
  if (!claims.exp || claims.exp <= now) {
    throw new Error('id_token is expired');
  }
  if (claims.token_use && claims.token_use !== 'id') {
    throw new Error(`Unexpected token_use: ${claims.token_use}`);
  }
  if (nonce && claims.nonce !== nonce) {
    throw new Error('id_token nonce mismatch');
  }

  return claims;
};

export default validateIdToken;
